import { type IRowNode } from "ag-grid-community"

import { AggregationMethod } from "~/components/reusable/AggregationSelector"
import { formatProductName } from "~/utils/formatting"

export const TOTALS_ROW_LABEL = "Totals"
const UNKNOWN_PRODUCT_NAME = "Unknown product"
const UNKNOWN_VARIANT_NAME = "Unknown variant"

export type RowNames = {
  productName?: string | null
  variantName?: string | null
}

/** Rows named from their data. Every other grouping (vendor, collection, extradata) groups by a name already. */
export const isNamedRowField = (field: string | null | undefined) =>
  field === "productId" || field === "variantId"

/**
 * The name a row shows. Shared by the identity cell and the CSV/xlsx export — exports never run
 * cell renderers, so a renderer-only label leaves the export carrying the raw group key.
 */
export const rowLabel = <T extends RowNames>(
  node: IRowNode<T> | null | undefined,
  aggregations: string[],
): string => {
  if (!node) return ""
  if (node.footer) return TOTALS_ROW_LABEL
  const isGroup = !!node.group
  if (isGroup && !isNamedRowField(node.field)) return node.key ?? ""

  // Every leaf of a product or variant group shares the names the group is labelled with.
  const entry = isGroup ? node.allLeafChildren?.find((c) => c.data != null)?.data : node.data
  const showsProductName = !aggregations.includes(AggregationMethod.ProductName)
  const { productName, needsBothNames } = resolveNames(entry ?? {}, showsProductName)

  if (!needsBothNames && isGroup && node.field === "productId") return productName
  return variantTitle(entry ?? {}, showsProductName)
}

const resolveNames = (names: RowNames, showsProductName: boolean) => {
  const productName = names.productName || UNKNOWN_PRODUCT_NAME
  // A variant with no name of its own is still identified by its product.
  const variantName = names.variantName || names.productName || UNKNOWN_VARIANT_NAME
  const needsBothNames =
    showsProductName && !variantName.includes(productName) && productName !== UNKNOWN_PRODUCT_NAME
  return { productName, variantName, needsBothNames }
}

/** A variant row's title outside the grid (e.g. a variant page's favorite). */
export const variantTitle = (names: RowNames, showsProductName = true) => {
  const { productName, variantName, needsBothNames } = resolveNames(names, showsProductName)
  return needsBothNames ? formatProductName(variantName, productName) : variantName
}
